"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InlineMathPopup = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const BasicButtonDelete_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton/BasicButtonDelete");
const BasicTooltip_1 = require("@jsonjoy.com/ui/lib/4-card/BasicTooltip");
const MiniTitle_1 = require("@jsonjoy.com/ui/lib/3-list-item/MiniTitle");
const ContextMenu_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu");
const ArgsPane_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu/ArgsPane");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const mathlive_1 = require("mathlive");
const use_t_1 = require("use-t");
const settings_1 = require("../components/blocks/math/settings");
const aspect_ratio_1 = tslib_1.__importDefault(require("iconista/lib/react/bootstrap/aspect-ratio"));
const tag_1 = tslib_1.__importDefault(require("iconista/lib/react/bootstrap/tag"));
require("mathlive");
require("mathlive/fonts.css");
require("mathlive/static.css");
const containerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    gap: '10px',
    pd: '12px 0 4px',
    w: '480px',
    maxW: '90vw',
});
const headerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    jc: 'space-between',
    gap: '8px',
    pd: '0 14px',
});
const headerActionsClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    gap: '4px',
});
const fieldWrapClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'flex',
    ai: 'center',
    w: '100%',
    minW: 0,
    pd: '4px 6px',
    bdrad: '6px',
    bxz: 'border-box',
    ov: 'hidden',
});
const fieldWrapOuterClass = (0, nano_theme_1.rule)({
    pd: '0 14px',
    minW: 0,
});
const mathFieldClass = (0, nano_theme_1.rule)({
    '&::part(content)': {
        ovx: 'auto',
        ovy: 'hidden',
    },
});
const SizeIcon = (props) => (React.createElement(aspect_ratio_1.default, { width: 16, height: 16, ...props }));
const LabelIcon = (props) => (React.createElement(tag_1.default, { width: 16, height: 16, ...props }));
const renderSizeIcon = () => React.createElement(SizeIcon, null);
const renderLabelIcon = () => React.createElement(LabelIcon, null);
const SIZE_OPTIONS = [
    { id: 'M', name: 'M — Script' },
    { id: 'S', name: 'S — Scriptscript' },
];
const safeConvert = (fn, tex) => {
    try {
        return fn(tex);
    }
    catch {
        return '';
    }
};
const InlineMathPopup = ({ state }) => {
    const [t] = (0, use_t_1.useT)();
    const styles = (0, context_1.useStyles)();
    const val = state.draftVal.use();
    const size = state.draftSize.use();
    const label = state.draftLabel.use();
    const editingPath = state.editingPath.use();
    const fieldRef = React.useRef(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
    React.useEffect(() => {
        const el = fieldRef.current;
        if (!el)
            return;
        if (typeof el.value === 'string' && el.value !== val) {
            el.value = val;
        }
        const raf = requestAnimationFrame(() => {
            try {
                el.focus?.();
            }
            catch { }
        });
        return () => cancelAnimationFrame(raf);
    }, []);
    React.useEffect(() => {
        const el = fieldRef.current;
        if (!el)
            return;
        const onInput = () => {
            const next = el.value ?? '';
            state.setDraftVal(next);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                state.apply();
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                state.close();
            }
        };
        el.addEventListener('input', onInput);
        el.addEventListener('keydown', onKeyDown);
        return () => {
            el.removeEventListener('input', onInput);
            el.removeEventListener('keydown', onKeyDown);
        };
    }, [state]);
    const isDark = !styles.light;
    const fieldColor = styles.g(0.15);
    const fieldBg = isDark ? styles.g(1, 0.04) : styles.g(0, 0.04);
    const fieldBorder = isDark ? styles.g(1, 0.1) : styles.g(0, 0.08);
    const sizeFontSize = settings_1.MATH_SIZE_FONT[size];
    const onCancel = React.useCallback(() => state.close(), [state]);
    const onRemove = React.useCallback(() => state.remove(), [state]);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
    }, []);
    const ascii = React.useMemo(() => (val ? safeConvert(mathlive_1.convertLatexToAsciiMath, val) : ''), [val]);
    const mathMl = React.useMemo(() => (val ? safeConvert(mathlive_1.convertLatexToMathMl, val) : ''), [val]);
    const mathHtml = React.useMemo(() => (val ? safeConvert(mathlive_1.convertLatexToMarkup, val) : ''), [val]);
    const item = React.useMemo(() => ({ name: t('Equation options'), compact: true }), [t]);
    const params = React.useMemo(() => {
        const list = [];
        list.push({
            kind: 'select',
            id: 'size',
            name: t('Size'),
            icon: renderSizeIcon,
            default: size,
            options: SIZE_OPTIONS,
        });
        list.push({
            kind: 'str',
            id: 'label',
            name: t('Label'),
            icon: renderLabelIcon,
            optional: true,
            placeholder: t('e.g. eq:pythagoras'),
            default: label,
        });
        if (val) {
            list.push({ name: t('Source'), heading: true, collapsible: true, initialCollapsed: true });
            list.push({
                kind: 'code',
                id: 'tex',
                name: 'LaTeX',
                variant: 'block',
                value: val,
            });
            if (ascii) {
                list.push({
                    kind: 'code',
                    id: 'ascii',
                    name: 'ASCII Math',
                    variant: 'block',
                    value: ascii,
                });
            }
            if (mathMl) {
                list.push({
                    kind: 'code',
                    id: 'mathMl',
                    name: 'MathML',
                    variant: 'block',
                    value: mathMl,
                });
            }
            if (mathHtml) {
                list.push({
                    kind: 'code',
                    id: 'mathHtml',
                    name: 'HTML',
                    variant: 'block',
                    value: mathHtml,
                });
            }
        }
        return list;
    }, [t, size, label, val, ascii, mathMl, mathHtml]);
    const onChange = React.useCallback((_list, map) => {
        const nextSize = map.size === 'S' ? 'S' : 'M';
        if (nextSize !== state.draftSize.value)
            state.setDraftSize(nextSize);
        const nextLabel = map.label ?? '';
        if (nextLabel !== state.draftLabel.value)
            state.setDraftLabel(nextLabel);
    }, [state]);
    return (React.createElement(ContextMenu_1.ContextPane, null,
        React.createElement("div", { className: containerClass },
            React.createElement("div", { className: headerClass },
                React.createElement(MiniTitle_1.MiniTitle, null, editingPath ? t('Edit equation') : t('Add equation')),
                editingPath && (React.createElement("div", { className: headerActionsClass },
                    React.createElement(BasicTooltip_1.BasicTooltip, { nowrap: true, renderTooltip: () => t('Remove equation') },
                        React.createElement(BasicButtonDelete_1.BasicButtonDelete, { type: "button", width: 32, height: 32, rounder: true, onMouseDown: preventMouseDown, onConfirm: onRemove }))))),
            React.createElement("div", { className: fieldWrapOuterClass },
                React.createElement("div", { className: fieldWrapClass, style: {
                        background: fieldBg,
                        border: `1px solid ${fieldBorder}`,
                        colorScheme: isDark ? 'dark' : 'light',
                        fontSize: sizeFontSize,
                    } }, React.createElement('math-field', {
                    ref: fieldRef,
                    'default-mode': 'inline-math',
                    className: mathFieldClass,
                    style: {
                        display: 'block',
                        flex: '1 1 0',
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        padding: 0,
                        color: fieldColor,
                        background: 'transparent',
                        ['--primary']: fieldColor,
                        ['--text-font-family']: 'inherit',
                        ['--caret-color']: fieldColor,
                        ['--keycap-background']: isDark ? styles.g(1, 0.08) : styles.g(0, 0.06),
                        ['--keycap-background-hover']: isDark ? styles.g(1, 0.16) : styles.g(0, 0.1),
                        ['--keycap-text']: fieldColor,
                        ['--keyboard-background']: isDark ? styles.g(1, 0.04) : styles.g(0, 0.04),
                        ['--keyboard-text']: fieldColor,
                        ['--smart-fence-color']: fieldColor,
                    },
                }))),
            React.createElement("div", null,
                React.createElement(ArgsPane_1.ArgsPane, { inline: true, item: item, params: params, onCancel: onCancel, onChange: onChange })))));
};
exports.InlineMathPopup = InlineMathPopup;
